// Critical-fields contract — see aegle-docs critical_fields_contract.md
import { Identifier } from "react-admin";
import { Claimant, ClaimsSummaryRow, Reimbursement, StoplossClaim } from "@/types";
import {
  getEffectiveDeductible,
  isLaseredDeductible,
  getResolvedASDValues,
  getResolvedClaimsValues,
} from "@/resources/claimants/hooks/useCurrentPolicyData";
import { isClaimInReportingPeriod, reimbursementDate } from "./reportingPeriod";

// ==================== SHARED HELPERS ====================
// Used by both summary and watch list calculations

/**
 * Bucket key for filed amounts on claims with no administering vendor (#5326).
 * Renders as an "Unassigned" column on the exhibit.
 */
export const UNASSIGNED_VENDOR_KEY = "__unassigned__";

/**
 * Groups a claim list's filed amount (total_paid_amount) by administering vendor
 * (vendor_id_ref), bucketing unstamped claims under UNASSIGNED_VENDOR_KEY (#5326).
 */
const filedByVendor = (claims: StoplossClaim[]): Record<string, number> =>
  claims.reduce<Record<string, number>>((acc, c) => {
    const key = c.vendor_id_ref || UNASSIGNED_VENDOR_KEY;
    acc[key] = (acc[key] ?? 0) + (c.total_paid_amount ?? 0);
    return acc;
  }, {});

/**
 * Checks if a date string falls within a date range (inclusive).
 *
 * For **date-only** (`DATE`) columns such as `claim_items.paid_date` and the
 * reimbursement dates (`reimbursementDate`), where both sides parse as UTC midnight and the
 * comparison cancels out. Do **not** use this for a `TIMESTAMPTZ` value — an instant
 * compared against UTC midnight lands in the wrong calendar day for anything created
 * after ~18:00 Central. Use `isClaimInReportingPeriod` / `toReportingDate` from
 * `./reportingPeriod` for those (#6823).
 */
export const isDateInRange = (dateStr: string | null | undefined, start: Date, end: Date): boolean => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  return !isNaN(date.getTime()) && start <= date && date <= end;
};

// ==================== SUMMARY CALCULATION ====================

export interface CalculateSummaryRowsParams {
  claimants: Claimant[];
  stoplossClaims: StoplossClaim[];
  reimbursements: Reimbursement[];
  policyId: Identifier;
  specificDeductible: number;
  startDate: string;
  endDate: string;
}

/**
 * Calculates claims summary rows — one row per claimant who is **at or over their
 * effective specific deductible**.
 *
 * ## Population (claimant grain)
 *
 * A claimant appears when their policy-year spend reaches their effective deductible
 * (the standard specific deductible, or the lasered override when lasered). This is
 * the same spend-vs-deductible test the Watch List applies, which deliberately stops
 * at 100% (`calculateWatchListRows`: "Watch list range: 50% inclusive to under 100%")
 * and hands the >=100% population here. The Summary previously keyed on "has a
 * stop-loss claim in range" instead, so a claimant who had breached their deductible
 * but had nothing filed yet was invisible.
 *
 * Because `total_paid` is a **policy-year** figure, this population is independent of
 * the selected date range. The range scopes the *dollar* columns, not who appears —
 * so a claimant over their deductible with no claims filed in the period shows with
 * $0 Owed. That is intentional: it surfaces breached-but-unfiled claimants.
 *
 * ## Claim grain
 *
 * The Claims Detail table and the export's Claims Detail sheet are claim-grain and
 * list the stop-loss claims created in the range (#6823) — a different question from
 * the one this function answers. Per-claimant Owed still equals that claimant's
 * in-range claims, but the two tables' totals are not expected to tie out, because
 * their populations differ by design.
 *
 * Column data sources:
 * - **Total YTD**: the authority-resolved policy-year spend via `getResolvedClaimsValues` (calculated = lines inside the basis window, #7338; reported = the carrier's YTD)
 * - **Deductible**: Policy specific deductible (or lasered override)
 * - **Owed**: Sum of SL claim `total_paid_amount` values in range — these already represent
 *   the incremental excess over the deductible for each filing
 * - **Medical / RX**: SL claim amounts grouped by type (filed breakdown)
 * - **Variance**: Owed − Reimbursed − Denied
 */
export const calculateClaimsSummaryRows = ({
  claimants,
  stoplossClaims,
  reimbursements,
  policyId,
  specificDeductible,
  startDate,
  endDate,
}: CalculateSummaryRowsParams): ClaimsSummaryRow[] => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const policyIdStr = String(policyId);

  // Step 1: Filter stoploss claims to those CREATED in the reporting period (#6823).
  // These scope the dollar columns; they no longer decide who appears.
  const claimsInRange = stoplossClaims.filter((claim) => isClaimInReportingPeriod(claim, startDate, endDate));

  // Step 2: Group the in-range claims by claimant, so each row can pick up its own.
  const claimsByClaimant = new Map<string, StoplossClaim[]>();
  for (const claim of claimsInRange) {
    const claimantId = String(claim.claimant_id_ref);
    const existing = claimsByClaimant.get(claimantId) ?? [];
    existing.push(claim);
    claimsByClaimant.set(claimantId, existing);
  }

  // The base arm of the precedence chain, built once. `specificDeductible` is
  // already `selectedPolicy.specific_benefit.specific_deductible`
  // (useClaimsSummaryData), i.e. exactly the field the resolver falls back to.
  const baseSource = { specific_benefit: { specific_deductible: specificDeductible } };

  // Step 3: Build one row per claimant at or over their effective deductible.
  const rows: ClaimsSummaryRow[] = [];

  for (const claimant of claimants) {
    const claimantId = String(claimant.id);
    const policyData = claimant.policy_data?.[policyIdStr];

    // Not enrolled on the selected policy — no deductible to measure against.
    if (!policyData) continue;

    // Total YTD: full policy-year health plan spend. Use the shared resolver —
    // bundled claimants get the authority-selected ledger; a carve-out claimant
    // gets the server-blended total (ADR-077 §5), where no single ledger holds
    // the true total.
    const totalYTD = getResolvedClaimsValues(policyData)?.total_paid ?? 0;

    // Effective deductible BEFORE the population test, via the SHARED resolver
    // (#6929). This previously re-derived the precedence inline from `is_lasered` /
    // `lasered_attributes`, which read neither `is_contract_lasered` nor
    // `is_contract_excluded` nor the BE-resolved `effective_specific_deductible` --
    // so a contract-lasered claimant (#5287) was measured against the policy BASE,
    // and a fully-excluded one appeared at all. ADR-077: duplicated resolvers
    // silently diverge, and this one had diverged from the five components that
    // already routed through `getEffectiveDeductible`.
    //
    // A laser sets an individual-specific deductible that may be ABOVE or BELOW the
    // policy default — direction is a per-claimant underwriting decision, not a rule
    // — so the claimant is measured against their own threshold and this code stays
    // direction-agnostic. That matches the server: `spend_over_deductible` is
    // `max(0, total_paid - EFFECTIVE deductible)` (per_type_authority.go), so the
    // population test and the excess figure agree whichever way the laser went.
    const {
      amount: claimantDeductible,
      source: deductibleSource,
      reviewPending,
    } = getEffectiveDeductible(policyData, baseSource);

    // A laser FLAG with no amount (#5901 split-brain) resolves to BASE inside the
    // resolver rather than to 0, so such a claimant is no longer silently dropped
    // by the non-positive guard below. `source` also distinguishes a contract
    // laser from a straight one, which the chip and the export need.
    // A review decision that chose the base is not a laser (isLaseredDeductible).
    const isLasered = isLaseredDeductible({ source: deductibleSource, amount: claimantDeductible }, baseSource);

    // A non-positive deductible cannot be breached meaningfully — every claimant with
    // any spend would qualify, flooding the exhibit. Mirrors the Watch List's guard
    // (`calculateWatchListRows`), so an unconfigured deductible is excluded from BOTH
    // tables rather than appearing in one and not the other.
    //
    // This now also covers a FULLY EXCLUDED claimant, whose effective deductible is a
    // legitimate 0 (`queries/policy_data.sql`: `WHEN is_contract_excluded THEN 0`).
    // They have no coverage, so they are not a claims-summary row -- which is the
    // same answer the server gives, where their excess is 0.
    if (claimantDeductible <= 0) continue;

    // Population test: at or over the effective deductible. Policy-year spend, so
    // deliberately not scoped by the reporting period.
    if (totalYTD < claimantDeductible) continue;

    // In-range claims for this claimant — may be empty, in which case the dollar
    // columns are $0 and the row still shows.
    const claims = claimsByClaimant.get(claimantId) ?? [];

    // Aggregate stoploss claim amounts by type (filed breakdown)
    const medicalClaims = claims.filter((c) => c.stoploss_claim_type === "medical");
    const rxClaims = claims.filter((c) => c.stoploss_claim_type === "rx");
    const totalMedical = medicalClaims.reduce((sum, c) => sum + (c.total_paid_amount ?? 0), 0);
    const totalRx = rxClaims.reduce((sum, c) => sum + (c.total_paid_amount ?? 0), 0);

    // Per-administering-vendor filed breakdown (#5326, Option B): each claim carries
    // one vendor, so grouping by vendor_id_ref splits the same totals above into the
    // per-TPA / per-RX-vendor exhibit columns. Claims with no vendor land in the
    // UNASSIGNED bucket. These sum back to totalMedical / totalRx by construction.
    const medicalFiledByVendor = filedByVendor(medicalClaims);
    const rxFiledByVendor = filedByVendor(rxClaims);

    // Owed = total filed with carrier (SL claim amounts are already excess over deductible)
    const totalOwed = totalMedical + totalRx;

    // Aggregate Specific Deductible drawdown (#3302, projected by #6829). On an
    // ASD policy this is what explains an Owed of $0: the claimant is over their
    // specific deductible, but the excess has been absorbed by the corridor and
    // is not yet carrier-payable. The exhibit hides these columns entirely on a
    // non-ASD policy (`showAggSpec`).
    //
    // Read straight from the engine. Until #6829 the amounts had a writer and no
    // reader — they arrived as 0 for every claimant — so this block carried an
    // `engineAllocated` probe, a `spend_over_deductible` fallback for the excess,
    // and an accumulating-only derivation of the split, plus a
    // `reconcileDerivedASDSplit` guard to withdraw the derivation when the
    // corridor status and the per-claimant snapshots disagreed. All of it is
    // deleted: two sources of truth for the same figure is the drift this
    // codebase keeps getting bitten by, and the derivation could only ever be
    // exact while `accumulating`, which left a breached corridor showing dashes
    // for money the carrier owed (#6866).
    //
    // undefined means "no allocation" and renders a dash; 0 is a real, meaningful
    // "nothing absorbed" or "nothing payable". The backend keeps those
    // distinguishable — the fields are nullable columns on the wire, so an
    // un-allocated claimant arrives with the keys absent rather than zeroed.
    const asd = getResolvedASDValues(policyData);
    const specificExcess = asd?.amt_specific_excess;
    const asdConsumed = asd?.amt_asd_consumed;
    const carrierPayable = asd?.amt_carrier_payable;

    // Filter reimbursements for this claimant within date range, on the report's one
    // reimbursement-date precedence (#7560). `check_date` alone dropped every
    // document-loaded reimbursement: loads write only `payment_date`.
    const claimantReimbs = reimbursements.filter((r) => {
      if (r.claimant_id_ref !== claimant.id) return false;
      return isDateInRange(reimbursementDate(r), start, end);
    });

    const totalRequested = claimantReimbs.reduce((sum, r) => sum + (r.requested_amount || 0), 0);
    const totalReimbursed = claimantReimbs.reduce((sum, r) => sum + (r.reimbursement_amount || 0), 0);
    const totalPended = claimantReimbs.reduce((sum, r) => sum + (r.pended_amount || 0), 0);
    const totalDenied = claimantReimbs.reduce((sum, r) => sum + (r.denial_amount || 0), 0);

    const variance = totalOwed - totalReimbursed - totalDenied;

    rows.push({
      id: claimantId,
      name: claimant.claimant_name ?? "Unknown Claimant",
      memberId: claimant.member_id,
      totalMedical,
      totalRX: totalRx,
      medicalFiledByVendor,
      rxFiledByVendor,
      totalYTD,
      specDeductible: claimantDeductible,
      totalOwed,
      specificExcess,
      asdConsumed,
      carrierPayable,
      totalRequested,
      totalPended,
      totalReimbursed,
      totalDenied,
      variance,
      isLasered,
      isContractLasered: deductibleSource === "CONTRACT",
      isDeductibleReviewPending: reviewPending,
      policyId: policyIdStr,
      claimCount: claims.length,
      claimantNote: policyData?.note,
    });
  }

  return rows;
};
